import type { CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import { BarChart3, ChefHat, ClipboardCheck, ShoppingCart, Truck, Warehouse } from 'lucide-react';
import { cn } from '@/utils/cn';

interface Stage {
  icon: LucideIcon;
  title: string;
  text: string;
}

/** Этапы учёта в том порядке, в каком через них проходят товар и деньги; иконки — как в меню приложения */
const STAGES: Stage[] = [
  { icon: Truck, title: 'Поставка', text: 'Приходные накладные поставщиков\u00A0— товар и цена попадают на склад.' },
  { icon: Warehouse, title: 'Склад', text: 'Приходы, перемещения и списания с проведением и контролем остатков.' },
  { icon: ChefHat, title: 'Производство', text: 'Выпуск продукции по техкартам\u00A0— себестоимость считается автоматически.' },
  { icon: ShoppingCart, title: 'Продажа', text: 'Касса точек и оптовые отгрузки по договорам, возвраты по чекам.' },
  { icon: ClipboardCheck, title: 'Инвентаризация', text: 'Пересчёт склада\u00A0— расхождения проводятся при утверждении.' },
  { icon: BarChart3, title: 'Отчёты', text: 'Остатки, оборот, ABC/XYZ-анализ и рентабельность по точкам.' },
];

/** Один этап цикла — 1,4 с из 10 с: столько же отводят ключевые кадры cycle-token и cycle-node */
const STAGE_SECONDS = 1.4;

interface AccountingCycleProps {
  /**
   * Движущийся вариант для широкого экрана: отметка проходит этапы по очереди и возвращается
   * по дуге к началу. Строки фиксированной высоты (--cycle-row), чтобы отметка попадала в узлы.
   * Без него — спокойный список для телефона: строки по высоте текста, без движения.
   */
  animated?: boolean;
  className?: string;
}

export function AccountingCycle({ animated = false, className }: AccountingCycleProps) {
  if (!animated) {
    return (
      <ol aria-label="Цикл учёта" className={cn('space-y-0', className)}>
        {STAGES.map((stage, index) => (
          <li
            key={stage.title}
            className={cn(
              'relative flex items-start gap-4 pb-5 last:pb-0',
              // Соединительная линия от узла к следующему этапу
              index < STAGES.length - 1 &&
                'after:absolute after:bottom-0 after:left-5 after:top-11 after:w-px after:-translate-x-1/2 after:bg-border'
            )}
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-surface text-muted-foreground">
              <stage.icon className="h-[18px] w-[18px]" aria-hidden />
            </span>
            <div className="min-w-0 pt-0.5">
              <p className="text-[15px] font-semibold leading-snug text-foreground">{stage.title}</p>
              <p className="mt-0.5 text-pretty text-[13.5px] leading-relaxed text-muted-foreground">{stage.text}</p>
            </div>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ol
      aria-label="Цикл учёта"
      className={cn(
        // Шаг рельса: на узкой плоскости (1024–1279px) описания переносятся на две строки — шаг больше;
        // с 1280px описания в одну строку; на очень широких экранах — чуть больше
        'relative ml-4 [--cycle-return:-34px] [--cycle-row:5.25rem] xl:[--cycle-row:4.75rem] min-[1800px]:[--cycle-row:5rem]',
        className
      )}
    >
      {/* Рельс через центры узлов; узел стоит у верхнего края строки, его центр — 20px */}
      <span
        aria-hidden
        className="absolute bottom-[calc(var(--cycle-row)-20px)] left-5 top-5 w-px -translate-x-1/2 bg-border"
      />
      {/* Возвратная дуга от «Отчётов» к «Поставке» — учёт идёт по кругу. Пунктиром и темнее рельса,
          чтобы обратный путь читался отдельно от прямого */}
      <span
        aria-hidden
        className="absolute bottom-[calc(var(--cycle-row)-20px)] left-[-14px] top-5 w-[34px] rounded-l-[14px] border border-r-0 border-dashed border-muted-foreground/45"
      />
      {/* Отметка: проходит по рельсу под узлами и возвращается по дуге */}
      <span
        aria-hidden
        className="absolute left-[15px] top-[15px] z-0 h-2.5 w-2.5 rounded-full bg-brand shadow-[0_2px_6px_-1px_hsl(var(--brand)/0.6)] ring-[3px] ring-background motion-reduce:hidden motion-safe:animate-cycle-token"
      />

      {STAGES.map((stage, index) => (
        <li key={stage.title} className="relative flex h-[var(--cycle-row)] items-start gap-4">
          <span
            className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-surface text-muted-foreground motion-safe:animate-cycle-node"
            style={{ animationDelay: `${index * STAGE_SECONDS}s` } as CSSProperties}
          >
            <stage.icon className="h-[18px] w-[18px]" aria-hidden />
          </span>
          {/* Текст по верху строки: название — на уровне центра узла, шаг названий ровный.
              От 1800px название и описание — в одну строку, чтобы цикл занял ширину плоскости */}
          <div className="min-w-0 pt-2.5 min-[1800px]:flex min-[1800px]:items-baseline min-[1800px]:gap-6">
            <p className="text-[15px] font-semibold leading-snug text-foreground min-[1800px]:w-40 min-[1800px]:shrink-0 min-[1800px]:text-base">
              {stage.title}
            </p>
            <p className="mt-0.5 line-clamp-2 max-w-[64ch] text-sm leading-relaxed text-muted-foreground min-[1800px]:mt-0 min-[1800px]:max-w-none min-[1800px]:text-[15px]">
              {stage.text}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
